import { useState, useRef, useEffect } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import { useToast } from "@/components/base/Toast";
import ReportsKPICards from "./components/ReportsKPICards";
import LeadSourcePerformance from "./components/LeadSourcePerformance";
import ConversionFunnel from "./components/ConversionFunnel";
import AgentPerformanceTable from "./components/AgentPerformanceTable";
import MonthlyTrendChart from "./components/MonthlyTrendChart";
import PipelineVelocity from "./components/PipelineVelocity";
import ActivityIntelligence from "./components/ActivityIntelligence";
import { exportToCSV, getMonthYearLabel } from "@/utils/exportUtils";
import { fetchApi } from "@/services/apiService";
import {
  sourcePerformanceData,
  funnelData,
  agentPerformanceData,
  pipelineVelocityData,
  monthlyTrendData,
} from "@/mocks/reportsData";

type Period = "week" | "month" | "quarter" | "custom";

const periods: { key: Period; label: string }[] = [
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "custom", label: "Custom" },
];

export default function ReportsPage() {
  const { showToast } = useToast();
  const [period, setPeriod] = useState<Period>("month");
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<string>("pdf");
  const [reportSummary, setReportSummary] = useState<Record<string, unknown> | null>(null);
  const hasData = true;

  useEffect(() => {
    fetchApi('/reports/summary', undefined, null)
      .then((data) => {
        if (data) setReportSummary(data as Record<string, unknown>);
      })
      .catch((err) => console.error("Error fetching reports summary:", err));
  }, []);

  const monthLabel = getMonthYearLabel();

  // ── Global Export ──
  const handleGlobalExport = () => {
    showToast("Preparing export...", "info");
    setExportModalOpen(false);

    if (exportFormat === "pdf") {
      setTimeout(() => window.print(), 500);
      return;
    }

    let rows: Record<string, unknown>[] = [];
    let filename = "";

    switch (exportFormat) {
      case "csv-kpi":
        rows = [
          { Metric: "Total Leads", Value: "847" },
          { Metric: "Connected Calls", Value: "634" },
          { Metric: "WhatsApp Messages", Value: "1,423" },
          { Metric: "Deals Won", Value: "89" },
          { Metric: "Revenue", Value: "₹71K" },
        ];
        filename = `kpi-summary-${monthLabel}.csv`;
        break;
      case "csv-source":
        rows = sourcePerformanceData.map((s) => ({
          Source: s.source,
          "Leads Count": s.count,
          Percentage: `${s.percentage}%`,
          "Conversion Rate": "",
        }));
        filename = `source-performance-${monthLabel}.csv`;
        break;
      case "csv-agent":
        rows = agentPerformanceData.map((a) => ({
          Agent: a.agent,
          Assigned: a.assigned,
          Contacted: a.contacted,
          Converted: a.converted,
          Revenue: a.revenue,
          "Conv%": `${a.convRate}%`,
        }));
        filename = `agent-performance-${monthLabel}.csv`;
        break;
      case "csv-monthly":
        rows = monthlyTrendData.map((m) => ({
          Month: m.month,
          Revenue: m.revenue,
          Leads: m.leads,
        }));
        filename = `monthly-trend-${monthLabel}.csv`;
        break;
    }

    setTimeout(() => {
      exportToCSV(rows, filename);
      showToast("Download started", "success");
    }, 800);
  };

  // ── Section Exports ──
  const exportSourcePerformance = () => {
    showToast("Preparing export...", "info");
    const rows = sourcePerformanceData.map((s) => ({
      Source: s.source,
      "Leads Count": s.count,
      Percentage: `${s.percentage}%`,
      "Conversion Rate": "",
    }));
    setTimeout(() => {
      exportToCSV(rows, `source-performance-${monthLabel}.csv`);
      showToast("Download started", "success");
    }, 600);
  };

  const exportFunnel = () => {
    showToast("Preparing export...", "info");
    let prevPct = 100;
    const rows = funnelData.map((s, i) => {
      const dropoff = i > 0 ? `${prevPct - s.percentage}%` : "—";
      prevPct = s.percentage;
      return {
        Stage: s.stage,
        Count: s.count,
        "Conversion%": `${s.percentage}%`,
        "Dropoff%": dropoff,
      };
    });
    setTimeout(() => {
      exportToCSV(rows, `conversion-funnel-${monthLabel}.csv`);
      showToast("Download started", "success");
    }, 600);
  };

  const exportAgentPerf = () => {
    showToast("Preparing export...", "info");
    const rows = agentPerformanceData.map((a) => ({
      Agent: a.agent,
      Assigned: a.assigned,
      Contacted: a.contacted,
      Converted: a.converted,
      Revenue: a.revenue,
      "Conv%": `${a.convRate}%`,
    }));
    setTimeout(() => {
      exportToCSV(rows, `agent-performance-${monthLabel}.csv`);
      showToast("Download started", "success");
    }, 600);
  };

  const exportVelocity = () => {
    showToast("Preparing export...", "info");
    const rows = pipelineVelocityData.map((s) => ({
      Stage: s.stage,
      "Avg Time": s.avgTime,
      "Leads In": s.leadsIn,
      "Leads Out": s.leadsOut ?? "—",
      "Dropoff%": s.dropOff ?? "—",
      "Health Status": s.health === "none" ? "—" : s.health,
    }));
    setTimeout(() => {
      exportToCSV(rows, `pipeline-velocity-${monthLabel}.csv`);
      showToast("Download started", "success");
    }, 600);
  };

  const exportMonthlyTrend = () => {
    showToast("Preparing export...", "info");
    const rows = monthlyTrendData.map((m) => ({
      Month: m.month,
      Revenue: m.revenue,
      Leads: m.leads,
    }));
    setTimeout(() => {
      exportToCSV(rows, `monthly-trend-${monthLabel}.csv`);
      showToast("Download started", "success");
    }, 600);
  };
  // ── End export handlers ──

  return (
    <CrmLayout title="Reports">
      <div className="p-5 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports &amp; Analytics</h1>
            <p className="text-sm text-gray-500 mt-0.5">Track your sales performance and team metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {periods.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`px-3.5 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    period === p.key
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setExportModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 active:scale-[0.98] whitespace-nowrap"
            >
              <i className="ri-download-line" /> Export Report
            </button>
          </div>
        </div>

        {!hasData ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <i className="ri-bar-chart-2-line text-4xl text-gray-300" />
            </div>
            <p className="text-lg font-semibold text-gray-700 mb-1">No data for this period</p>
            <p className="text-sm text-gray-500 mb-5">Try selecting a different date range</p>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              {periods.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  className={`px-3.5 py-2 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    period === p.key
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Row 1: KPI cards */}
            <ReportsKPICards />

            {/* Row 2: Source Performance + Conversion Funnel */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <LeadSourcePerformance onExport={exportSourcePerformance} />
              <ConversionFunnel onExport={exportFunnel} />
            </div>

            {/* Row 3: Agent Performance + Monthly Trend */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <AgentPerformanceTable onExport={exportAgentPerf} />
              <MonthlyTrendChart onExport={exportMonthlyTrend} />
            </div>

            {/* Row 4: Pipeline Velocity */}
            <PipelineVelocity onExport={exportVelocity} />

            {/* Row 5: Activity Intelligence */}
            <ActivityIntelligence />
          </>
        )}
      </div>

      {/* Export Modal */}
      {exportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl w-full max-w-sm shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Full Report</h3>

            <div className="space-y-3 mb-5">
              {[
                { key: "pdf", label: "PDF Report (full page with charts)", icon: "ri-file-pdf-2-line text-red-500" },
                { key: "csv-kpi", label: "CSV — KPI Summary", icon: "ri-file-excel-2-line text-green-600" },
                { key: "csv-source", label: "CSV — Source Performance data", icon: "ri-file-excel-2-line text-green-600" },
                { key: "csv-agent", label: "CSV — Agent Performance data", icon: "ri-file-excel-2-line text-green-600" },
                { key: "csv-monthly", label: "CSV — Monthly Trend data", icon: "ri-file-excel-2-line text-green-600" },
              ].map((opt) => (
                <label
                  key={opt.key}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                    exportFormat === opt.key
                      ? "border-indigo-300 bg-indigo-50"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value={opt.key}
                    checked={exportFormat === opt.key}
                    onChange={() => setExportFormat(opt.key)}
                    className="w-4 h-4 text-indigo-600 accent-indigo-600"
                  />
                  <i className={opt.icon} />
                  <span className="text-sm text-gray-700">{opt.label}</span>
                </label>
              ))}
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Exporting: {period === "week" ? "This Week" : period === "month" ? "This Month" : period === "quarter" ? "This Quarter" : "Custom"} data
            </p>

            <button
              onClick={handleGlobalExport}
              className="w-full px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors whitespace-nowrap mb-2"
            >
              Export
            </button>
            <button
              onClick={() => setExportModalOpen(false)}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </CrmLayout>
  );
}